import React, { useState, useEffect, useRef } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { userDataAtom } from "@/recoil/atom/userDataAtom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import Div100vh from "react-div-100vh";
import { fetchData, handleAndTrackError, getDataB2B } from "@/lib/helper";
import {
  fetchJourneyWithGates,
  evaluateGate,
  GATE_DECISION,
} from "@/b2b/lib/journeyGates";
import { rentDetailsFormDataAtom } from "@/recoil/atom/rentDetailsFormDataAtom";
import HomeFaqWhatsappHeader from "@/components/common/Header/HomeFaqWhatsappHeader.jsx";
import StepCard from "@/b2b/components/OnboardingStepsCard.jsx";
import FexPrimeLogo from "@/assets/icons/fexprime-logo-black.svg";
import { useJourneyLender } from "@/lib/journeyLender";
import { isWorkDetailsDone } from "@/b2b/lib/employmentRules";
import { workDetailsRules } from "@/b2b/lib/workDetailsLender";
import { lenderConfigAtom } from "@/recoil/atom/lenderConfigAtom";
import { areAllStepsComplete } from "@/lib/journeyCompletion";
import myGif from "@/assets/icons/loading.gif";
import Header from "@/b2b/components/Header";
import ProcessingScreen from "./processing";
import config from "@/config";
// import fp_logo from "@/"

export default function OnboardingSteps({ theme }) {
  const navigate = useNavigate();
  const socket = useRef(null);

  const [userData, setUserData] = useState(null);
  const [journeyData, setJourneyData] = useState(null);
  const lender = useJourneyLender(journeyData?.lender);
  // The work-details gate judges by THIS screen's own journey read only, never the session cache
  // (review I1): no lender read yet → today's rule (a work email is required).
  const lenderConfig = useRecoilValue(lenderConfigAtom);
  const lenderRules = workDetailsRules(lenderConfig?.lenders, journeyData?.lender ?? null);
  const [formData, setFormData] = useRecoilState(rentDetailsFormDataAtom);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [kycData, setKycData] = useState(null);
  const [bankingData, setBankingData] = useState(null);
  const [pennyDropData, setPennyDropData] = useState(null);
  const [esignData, setEsignData] = useState(null);

  const [searchParams] = useSearchParams();
  const partner = localStorage.getItem("partnerIdentifier");
  const userIdentifier = localStorage.getItem("user_identifier");

  useEffect(() => {
    if (!userDataLoading && !isLoading) {
      if (
        ["SUCCESS"].includes(kycData?.status) &&
        userData &&
        !isWorkDetailsDone(userData?.employment, lenderRules)
      ) {
        navigate(`/b/${userIdentifier}?partner=${partner}&step=trn`, {
          state: {
            name: "Work Details",
            path: `/b/${userIdentifier}?partner=${partner}&step=work-details`,
          },
        });
      } else if (areAllStepsComplete(journeyData?.steps)) {
        // Happy Move-In requires EVERY step of this journey to be complete — not just the
        // mandate. This branch used to read `emandateData?.status === "SUCCESS"` alone, and
        // because it sits above the KYC/banking/penny-drop/e-sign branches below, one step
        // decided the entire journey. A tenant whose EMANDATE step was wrongly marked SUCCESS
        // (see cpcore's cross-journey mandate bug) was shown a congratulations screen and
        // could no longer reach the steps they still owed.
        //
        // Derived from the journey's own `steps` array rather than a fixed list of names, so
        // partner-specific step sets keep working. See lib/journeyCompletion.js.
        navigate(`/b/${userIdentifier}?partner=${partner}&step=trn`, {
          state: {
            customMessage: "Congrats, you're all done 🚀",
            path: `/b/${userIdentifier}?partner=${partner}&step=happy-move-in`,
          },
        });
      } else if (
        ["SUCCESS", "IN_REVIEW"].includes(kycData?.status) &&
        bankingData?.status === "SUCCESS" &&
        pennyDropData?.status === "PENDING"
      ) {
        navigate(`/b/${userIdentifier}?partner=${partner}&step=trn`, {
          state: {
            name: "Penny Drop",
            path: `/b/${userIdentifier}?partner=${partner}&step=penny-drop`,
          },
        });
      } else if (
        kycData?.status === "SUCCESS" &&
        bankingData?.status === "SUCCESS" &&
        pennyDropData?.status === "SUCCESS" &&
        esignData?.status === "SUCCESS"
      ) {
        navigate(`/b/${userIdentifier}?partner=${partner}&step=trn`, {
          state: {
            name: "Mandate",
            path: `/b/${userIdentifier}?partner=${partner}&step=mandate`,
          },
        });
      }
    }
  }, [isLoading, userDataLoading]);

  const getData = async (rentSummaryConsent) => {
    try {
      setIsLoading(true);
      // Same call as before — `gates` rides on the existing /journey/full response, so reading
      // the server's verdict costs no extra request.
      const { journey, gates } = await fetchJourneyWithGates();

      setJourneyData(journey);

      const bankingStep = journey.steps?.find(
        (step) => step.actor === "USER" && step.name === "BANKING"
      );

      // Same server gate `HomeScreen.jsx`'s resume logic checks before ITS banking/banking-review
      // routing (see journey-gates.ts on cpcore, `AppConfigKey.WORK_DETAILS_BANKING_GATE_MODE`).
      // This screen has two separate paths below that can also send a tenant to banking or
      // banking-review from `bankingStep.status` alone — checked once here, before either, since
      // a `false` verdict means "go to work-details", never "go to banking/banking-review".
      const workDetailsRequired =
        gates?.enforcement?.banking === "apply" && gates?.allowed?.banking === false;
      if (workDetailsRequired && bankingStep && bankingStep.status !== "SUCCESS") {
        navigate(`/b/${userIdentifier}?partner=${partner}&step=work-details`);
        return;
      }

      if (["IN_REVIEW", "WAITING"].includes(bankingStep?.status)) {
        navigate(`/b/${userIdentifier}?partner=${partner}&step=banking-review`);
        return;
      }

      // This screen presumes banking is done — it renders "Income Verification: Completed".
      //
      // The guard below already refuses a tenant whose banking is not SUCCESS, but ONLY when
      // `journey.status === "LOCKED"`, so an UNLOCKED journey skipped it entirely. That is the
      // hole a tenant fell through after being correctly refused Congratulations:
      // `bank_details` forwarded them here on the stale `Lead.bank_eligible` flag, and this
      // screen then told them their income was verified when it was not.
      //
      // The server's verdict applies regardless of journey status, and it reads the CURRENT
      // banking attempt rather than the shared step field that any of 13 writers can corrupt.
      const postBanking = evaluateGate(gates, "postBanking");
      if (postBanking.decision === GATE_DECISION.REDIRECT) {
        console.warn(
          "Onboarding steps withheld by the server gate:",
          postBanking.reason,
          "->",
          postBanking.fallbackStep,
        );
        navigate(
          `/b/${userIdentifier}?partner=${partner}&step=${postBanking.fallbackStep}`,
        );
        return;
      }

      if (journey.status === "LOCKED") {
        if (bankingStep?.status === "SUCCESS") {
          if (!rentSummaryConsent) {
            navigate(`/b/${userIdentifier}?partner=${partner}&step=trn`, {
              state: {
                name: "Rent Summary",
                path: `/b/${userIdentifier}?partner=${partner}&step=rent-summary`,
              },
            });
            return;
          }
          // rentSummaryConsent already done — fall through to load steps normally
        } else {
          navigate(`/b/${userIdentifier}?partner=${partner}&step=banking`);
          return;
        }
      }

      let journeyDataSteps = journey?.steps;

      setKycData(
        journeyDataSteps.find(
          (step) => step.actor === "USER" && step.name === "KYC"
        )
      );
      setBankingData(
        journeyDataSteps.find(
          (step) => step.actor === "USER" && step.name === "BANKING"
        )
      );
      setPennyDropData(
        journeyDataSteps.find(
          (step) => step.actor === "SYSTEM" && step.name === "PENNY_DROP"
        )
      );
      setEsignData(
        journeyDataSteps.find(
          (step) => step.actor === "USER" && step.name === "E_SIGN"
        )
      );
      // No separate EMANDATE state: completion is now decided from the journey's whole steps
      // array (areAllStepsComplete), so holding the mandate step on its own would only invite
      // someone to reintroduce the single-step shortcut this change removed.
      setData(journeyDataSteps?.filter((step) => step.actor === "USER"));
    } catch (error) {
      handleAndTrackError(error, {
        context: "Fetching journey data",
        endpoint: "journey",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const startOnboardingJourney = async () => {
      try {
        const token = localStorage.getItem("user_identifier");
        const result = await getDataB2B("b2b/onboard/start", token);

        if (result?.errorCode === 400) {
          navigate(`/b/${userIdentifier}`);
          return;
        }

        setFormData(result?.user);

        if (result?.token) {
          localStorage.setItem(config.LOCAL_KEYS.SIGNUP_TOKEN, result?.token);
          await Promise.all([getUserData(), getData(result?.user?.rentSummaryConsent)]);
        } else {
          navigate(`/b/${userIdentifier}`);
          return;
        }
      } catch (error) {
        console.error("Error fetching data", error);
      }
    };

    startOnboardingJourney();

    // if (!formData.rent) startOnboardingJourney();
  }, []);

  const getUserData = async () => {
    try {
      setUserDataLoading(true);
      const data = await fetchData("user/me");
      setUserData(data);
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    } finally {
      setUserDataLoading(false);
    }
  };

  // if (!isFetched && (!userData.phone || userData.approval?.length === 0)) {

  // setIsFetched(true);
  // }

  // const timeoutId = setTimeout(() => {
  //   setTimeoutReached(true);
  // }, 2000);
  // return () => clearTimeout(timeoutId);

  const filteredData = data.filter((step) => {
    // If step.name is "E_SIGN" and partner is "housr", skip it
    // if (step.name === "E_SIGN" && partner === "housr") {
    //   return false;
    // }
    // Otherwise keep the step
    return true;
  });

  if (isLoading || userDataLoading) return <ProcessingScreen timer recoverable />;
  else {
    return (
      <Div100vh
        className={`flex flex-col justify-between p-6 gap-8 overflow-y-scroll ${theme?.backgroundColor} ${theme?.textColor}`}
      >
        {/* <HomeFaqWhatsappHeader /> */}
        <Header />

        {/* {!isLoading ? (
          <div className="flex flex-col w-full h-full gap-2 aspect-square items-center justify-center text-[#bbb]">
            <div className="w-12 h-12 border-4 border-black border-t-white border-r-0 rounded-full animate-spin"></div>
            <span className="animate-pulse">Fetching...</span>
          </div>
        ) : ( */}
        <div className="flex flex-col">
          {filteredData.map((cardData, index) => (
            <React.Fragment key={index}>
              <StepCard
                data={cardData}
                theme={theme}
                setIsLoading={setIsLoading}
                lender={journeyData?.lender}
              />
              {index < filteredData.length - 1 && (
                <div
                  className={`w-px h-16 border-l-2 border-dashed ${theme?.darkBorderColor} ml-8`}
                ></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {lender && (
          <div className="flex items-center justify-center">
            <span className="text-black text-2xs italic">powered by</span>
            <a href={lender.homeUrl}>
              <img
                src={lender.logoUrl}
                alt={lender.legalName}
                className="inline-block max-h-3 ml-1"
              />
            </a>
          </div>
        )}
      </Div100vh>
    );
  }
}
